import React from 'react';
import { useMediaPerformance } from '../../../../../common/hooks/use-media-performance/use-media-performance';
import { MEDIA_TYPE } from '../constants/media';
import { useManagedMediaSource } from '../hooks/use-managed-media-source';
import { useMediaElementRef } from '../hooks/use-media-element-ref';
import { useSeekOnFrameChange } from '../hooks/use-seek-on-frame-change';
import { useUpdateIsPlaying } from '../hooks/use-update-is-playing';
import { useUpdatePlaybackRate } from '../hooks/use-update-playback-rate';
import { useUpdateVolume } from '../hooks/use-update-volume';
import { useVideoEventListeners } from '../hooks/use-video-event-listeners';
import type { VideoProps } from '../types';
import { StyledVideo } from './styled-video';
export const Video = ({
  id,
  videoType,
  mediaFrameRate,
  disablePointerEvents,
  brightness,
  contrast,
  onVideoElementRefChange: onVideoElementRefChangeProp,
  src: initialSrc,
  currentFrame,
  getTimeFromFrameNumber,
  isPlaying,
  volume,
  playbackRate,
  onSrcChange,
  getFrameNumberFromTime,
  onLoad,
  onLoadedMetadata,
  onDimensionChange,
  onFrameLoadStatus,
  onEndToLastFrame,
  onLoading,
  onError,
  fitToScreen,
  getSignedUrl,
  srcExpirationDuration,
  fetchFileSizeAndFormat,
  onLoadStart,
  ...otherVideoProps
}: VideoProps) => {
  const {
    mediaElement: videoElement,
    mediaElementRef: videoElementRef,
    onMediaElementRefChange: onVideoElementRefChange
  } = useMediaElementRef({
    onRefChange: onVideoElementRefChangeProp
  });
  const {
    src
  } = useManagedMediaSource({
    initialSrc,
    initialFrame: currentFrame,
    type: MEDIA_TYPE.VIDEO,
    getTimeFromFrameNumber,
    mediaType: videoType,
    onSrcChange,
    mediaElement: videoElement,
    getSignedUrl,
    fitToScreen,
    srcExpirationDuration,
    onLoading,
    onError,
    onLoadStart
  });
  useMediaPerformance(videoElementRef, {
    frameRate: mediaFrameRate,
    mediaUrl: src,
    mediaType: 'VIDEO'
  }, fetchFileSizeAndFormat);
  useVideoEventListeners({
    videoElement,
    getFrameNumberFromTime,
    onLoad,
    onLoadedMetadata,
    onDimensionChange,
    onFrameLoadStatus,
    onEndToLastFrame
  });
  useSeekOnFrameChange({
    videoElement,
    src,
    currentFrame,
    getTimeFromFrameNumber
  });
  useUpdateIsPlaying({
    mediaElement: videoElement,
    src,
    isPlaying
  });
  useUpdateVolume({
    mediaElement: videoElement,
    volume
  });
  useUpdatePlaybackRate({
    mediaElement: videoElement,
    playbackRate
  });
  return <StyledVideo id={id} src={src} ref={onVideoElementRefChange} videoType={videoType} disablePointerEvents={disablePointerEvents} brightness={brightness} contrast={contrast} {...otherVideoProps} />;
};